import type { TFunction } from 'i18next';

/** Row shape returned by the get_official_recency_flags RPC. */
export interface RecencyFlagRow {
  match_id: string;
  official_id: string;
  trigger_match_id: string;
  trigger_team_id: string;
  trigger_game_day: number;
  trigger_match_date: string;
  window_n: number;
  /** cup_rounds.code of the trigger match (migration 20261019101300); absent before it. */
  trigger_cup_round?: string | null;
  /** public.is_cup_match of the trigger match (migration 20261019101300); absent before it. */
  trigger_is_cup?: boolean | null;
}

export interface RecencyFlag {
  triggerMatchId: string;
  triggerTeamId: string;
  triggerGameDay: number;
  triggerMatchDate: string;
  windowN: number;
  triggerCupRound: string | null;
  /** null = unknown (RPC without the column): the target match decides, it is in the same group. */
  triggerIsCup: boolean | null;
}

/** matchId → (officialId → flag) */
export type RecencyFlagsByMatch = Map<string, Map<string, RecencyFlag>>;

export function buildRecencyFlagMaps(rows: RecencyFlagRow[]): RecencyFlagsByMatch {
  const byMatch: RecencyFlagsByMatch = new Map();
  for (const r of rows) {
    let inner = byMatch.get(r.match_id);
    if (!inner) {
      inner = new Map();
      byMatch.set(r.match_id, inner);
    }
    inner.set(r.official_id, {
      triggerMatchId: r.trigger_match_id,
      triggerTeamId: r.trigger_team_id,
      triggerGameDay: r.trigger_game_day,
      triggerMatchDate: r.trigger_match_date,
      windowN: r.window_n,
      triggerCupRound: r.trigger_cup_round ?? null,
      triggerIsCup: typeof r.trigger_is_cup === 'boolean' ? r.trigger_is_cup : null,
    });
  }
  return byMatch;
}

/**
 * Tour du match déclencheur quand c'est un match de coupe (« 2ème tour », « Tour non
 * précisé »…, via `cupLabel` — useRoundBuckets().cupLabel gère le chargement) ; null
 * pour un match de championnat, dont l'avertissement garde « J{n} ». Le déclencheur
 * est du même groupe que le match ciblé : sans trigger_is_cup (RPC d'avant
 * 20261019101300), le match ciblé décide.
 */
export function recencyTriggerRoundLabel(
  flag: RecencyFlag,
  opts: { targetIsCup: boolean; cupLabel: (round: string | null) => string },
): string | null {
  const isCup = flag.triggerIsCup ?? opts.targetIsCup;
  return isCup ? opts.cupLabel(flag.triggerCupRound) : null;
}

/**
 * Texte de l'avertissement de récence (infobulle ou confirmation), namespace planning.
 * `roundLabel` = recencyTriggerRoundLabel : null → textes d'origine « J{n} »,
 * sinon variantes « _round » avec le tour.
 */
export function recencyWarningText(
  t: TFunction,
  kind: 'tooltip' | 'confirm_message',
  flag: RecencyFlag,
  params: { team: string; official?: string },
  roundLabel: string | null,
): string {
  const base = { ...params, n: flag.windowN, date: flag.triggerMatchDate };
  return roundLabel === null
    ? t(`recency_warning.${kind}`, { ...base, gameDay: flag.triggerGameDay })
    : t(`recency_warning.${kind}_round`, { ...base, round: roundLabel });
}
